import React, { useEffect, useRef, useState } from 'react';

const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

type ScanState = 'scanning' | 'match' | 'nomatch' | 'error';

interface Result {
  status: string;
  name?: string;
  scan_type?: string;
  time?: string;
  message?: string;
}

export default function EmployeeKiosk() {
  const params = new URLSearchParams(window.location.search);
  const apiKey = params.get('key') || '';

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const intervalRef = useRef<any>(null);
  const countdownRef = useRef<any>(null);
  const scanLockRef = useRef(false);
  const [state, setState] = useState<ScanState>('scanning');
  const [result, setResult] = useState<Result | null>(null);
  const [faceApiLoaded, setFaceApiLoaded] = useState(false);
  const [time, setTime] = useState('');
  const [date, setDate] = useState('');
  const [cooldown, setCooldown] = useState(0);

  // Web Audio Chime (Zero MP3 files needed)
  const playSound = (type: 'success' | 'fail') => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      if (type === 'success') {
        osc.frequency.setValueAtTime(587.33, ctx.currentTime);
        osc.frequency.setValueAtTime(880, ctx.currentTime + 0.1);
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
        osc.start();
        osc.stop(ctx.currentTime + 0.35);
      } else {
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(180, ctx.currentTime);
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        osc.start();
        osc.stop(ctx.currentTime + 0.3);
      }
    } catch {}
  };

  // Clock & Screen Wake Lock (Keeps screen awake)
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setTime(now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }));
      setDate(now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }));
    };
    tick();
    const t = setInterval(tick, 1000);

    let wakeLock: any = null;
    if ('wakeLock' in navigator) {
      (navigator as any).wakeLock.request('screen').then((lock: any) => { wakeLock = lock; }).catch(() => {});
    }

    return () => {
      clearInterval(t);
      if (wakeLock) wakeLock.release().catch(() => {});
    };
  }, []);

  // Load face-api
  useEffect(() => {
    if ((window as any).faceapi) { setFaceApiLoaded(true); return; }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js';
    script.onload = async () => {
      const faceapi = (window as any).faceapi;
      const MODEL_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.13/model';
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      ]);
      setFaceApiLoaded(true);
    };
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!faceApiLoaded) return;
    startCamera();
    return () => stopCamera();
  }, [faceApiLoaded]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 1280, height: 720 } });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      startScanning();
    } catch {
      setState('error');
    }
  };

  const stopCamera = () => {
    clearInterval(intervalRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
  };

  const startScanning = () => {
  const faceapi = (window as any).faceapi;
  if (!faceapi) return;
  intervalRef.current = setInterval(async () => {
    if (scanLockRef.current) return;
    const v = videoRef.current;
    if (!v || v.readyState < 2) return;

    scanLockRef.current = true;
    const detection = await faceapi
      .detectSingleFace(v, new faceapi.TinyFaceDetectorOptions())
      .withFaceLandmarks()
      .withFaceDescriptor();
    if (!detection) {
      scanLockRef.current = false;
      return;
    }
    clearInterval(intervalRef.current);

    const descriptor = Array.from(detection.descriptor);
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const local_date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const local_time = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
    const local_time_12h = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });

    try {
      const res = await fetch(`${BASE}/employees/kiosk/employee/face-scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          descriptor,
          key: apiKey,
          local_date,
          local_time,
          local_time_12h,
        }),
      });

      const contentType = res.headers.get('content-type') || '';
      const data = contentType.includes('application/json') ? await res.json() : { detail: await res.text() };

      if (res.ok && data.status === 'success') {
        setResult(data);
        setState('match');
        playSound('success');
      } else if (res.status === 404) {
        setResult(data);
        setState('nomatch');
        playSound('fail');
      } else {
        setResult({ status: 'blocked', message: data.detail || data.message || 'Scan could not be completed.' });
        setState('error');
        playSound('fail');
      }
    } catch {
      setResult({ status: 'error', message: 'Connection error. Reconnecting...' });
      setState('error');
    }

    // ⚡ 3-second snappy cooldown (perfect for entrance queues)
    let secondsLeft = 3;
    setCooldown(secondsLeft);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      secondsLeft -= 1;
      setCooldown(secondsLeft);
      if (secondsLeft <= 0) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
        setCooldown(0);
        scanLockRef.current = false;
        setState('scanning');
        setResult(null);
        startScanning();
      }
    }, 1000);
  }, 800);
};

  if (!apiKey) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        flexDirection: 'column',
        gap: 16,
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}>
        <div style={{ fontSize: 48 }}>⚠️</div>
        <div style={{ fontSize: 20, fontWeight: 700 }}>Missing kiosk key</div>
        <div style={{ fontSize: 13, color: '#94a3b8' }}>URL must include ?key=YOUR_DEVICE_API_KEY</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#ffffff',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '20px 32px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 28 }}>👔</div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>Employee Attendance</div>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500, marginTop: 1 }}>
              Look at the camera to check in or out
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 32, fontWeight: 800, color: '#0f172a', letterSpacing: '0.05em', fontVariantNumeric: 'tabular-nums' }}>
            {time}
          </div>
          <div style={{ fontSize: 11, color: '#1e293b', fontWeight: 600, marginTop: 2 }}>{date}</div>
        </div>
      </div>

      {/* Main */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32, gap: 48 }}>

        {/* Camera */}
        <div style={{ position: 'relative', width: 520, height: 400, borderRadius: 24, overflow: 'hidden', flexShrink: 0, background: '#000' }}>
          <video
            ref={videoRef}
            autoPlay playsInline muted
            style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
          />
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
          }}>
            <div style={{
              width: 240, height: 300, borderRadius: '50%',
              border: `3px solid ${state === 'match' ? '#22c55e' : state === 'nomatch' ? '#ef4444' : '#6366f1'}`,
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
              transition: 'border-color 0.3s',
            }} />
          </div>
          {state === 'scanning' && faceApiLoaded && (
            <div style={{
              position: 'absolute', bottom: 16, left: 0, right: 0,
              textAlign: 'center', fontSize: 11, fontWeight: 700,
              color: '#818cf8', letterSpacing: '0.1em',
            }}>● SCANNING</div>
          )}
          {!faceApiLoaded && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(0,0,0,0.8)', color: '#64748b', fontSize: 13, fontWeight: 600, flexDirection: 'column', gap: 12,
            }}>
              <div style={{ fontSize: 32 }}>⏳</div>
              Loading face recognition...
            </div>
          )}
        </div>

        {/* Result */}
        <div style={{ width: 280 }}>
          {state === 'scanning' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 72, marginBottom: 20 }}>👤</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 10, lineHeight: 1.2 }}>
                Step up & face the camera
              </div>
              <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.7 }}>
                Your attendance is marked automatically. No PIN or card needed.
              </div>
            </div>
          )}

          {state === 'match' && result && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 80, marginBottom: 16, animation: 'pop 0.3s ease' }}>
                {result.scan_type === 'IN' ? '✅' : '🔴'}
              </div>
              <div style={{ fontSize: 30, fontWeight: 900, color: '#0f172a', marginBottom: 8, letterSpacing: '-0.02em' }}>
                {result.name}
              </div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '8px 20px', borderRadius: 99,
                background: result.scan_type === 'IN' ? 'rgba(34,197,94,0.12)' : 'rgba(251,146,60,0.12)',
                border: `1px solid ${result.scan_type === 'IN' ? '#22c55e' : '#fb923c'}`,
                color: result.scan_type === 'IN' ? '#22c55e' : '#fb923c',
                fontSize: 14, fontWeight: 800, marginBottom: 8, letterSpacing: '0.05em',
              }}>
                {result.scan_type === 'IN' ? '↗ CHECKED IN' : '↙ CHECKED OUT'}
              </div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{result.time}</div>
            </div>
          )}

          {state === 'nomatch' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 72, marginBottom: 16 }}>❓</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>Not recognized</div>
              <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
                Face not found. Use QR code or ask your manager to register your face.
              </div>
            </div>
          )}

          {state === 'error' && (
  <div style={{ textAlign: 'center' }}>
    <div style={{ fontSize: 72, marginBottom: 16 }}>❌</div>
    <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>
      {result?.status === 'blocked' ? 'Scan Not Allowed' : 'Error'}
    </div>
    <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
      {result?.message || 'Something went wrong. Please try again.'}
    </div>
  </div>
)}
          {state !== 'scanning' && cooldown > 0 && (
            <div style={{ marginTop: 20, textAlign: 'center', fontSize: 13, color: '#94a3b8', fontWeight: 700 }}>
              Next scan in {cooldown}s
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{
        padding: '12px 32px', textAlign: 'center',
        borderTop: '1px solid #e2e8f0',
        fontSize: 9, color: '#94a3b8', fontWeight: 700, letterSpacing: 2,
      }}>
        POWERED BY CLOUDAIRAMBO™
      </div>

      <style>{`
        @keyframes pop { 0% { transform: scale(0.5); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
      `}</style>
    </div>
  );
}
